/**
 * coachDom.ts — the anchor-resolution logic shared by CoachMarkProvider
 * (deciding whether a tour can start) and CoachMarkHost (tracking the
 * active step's anchor as it moves). Lifted verbatim from the v1
 * Provider's `anchorResolvesNow` so both consumers share one definition
 * instead of two copies that could drift.
 */

export function anchorElement(anchor: string): HTMLElement | null {
  try {
    return document.querySelector<HTMLElement>(`[data-coach="${anchor}"]`);
  } catch {
    return null;
  }
}

export interface CoachViewportSize {
  width: number;
  height: number;
}

// Rejects zero-area anchors and anchors sitting entirely outside the
// viewport — the second clause is what catches the off-canvas mobile
// drawer, which has a non-zero rect sitting at negative x.
export function rectIsVisible(rect: { top: number; left: number; right: number; bottom: number; width: number; height: number }, viewport: CoachViewportSize): boolean {
  if (rect.width === 0 || rect.height === 0) return false;
  if (rect.right <= 0 || rect.bottom <= 0 || rect.left >= viewport.width || rect.top >= viewport.height) {
    return false;
  }
  return true;
}

export function anchorResolvesNow(anchor: string): boolean {
  try {
    const el = anchorElement(anchor);
    if (!el) return false;
    const rect = el.getBoundingClientRect();
    return rectIsVisible(rect, { width: window.innerWidth, height: window.innerHeight });
  } catch {
    return false;
  }
}

export type CoachState = Record<string, string>;

/**
 * Page state the tour can condition cards on. Any element may declare
 * `data-coach-state="kind=apps"` (several pairs, space-separated). Later
 * elements in DOM order override earlier ones for the same key. Read every
 * measure — it is one querySelectorAll over a handful of elements.
 */
export function readCoachState(): CoachState {
  const state: CoachState = {};
  try {
    document.querySelectorAll<HTMLElement>('[data-coach-state]').forEach((el) => {
      const raw = el.getAttribute('data-coach-state') || '';
      for (const pair of raw.split(/\s+/)) {
        const eq = pair.indexOf('=');
        if (eq <= 0) continue;
        state[pair.slice(0, eq)] = pair.slice(eq + 1);
      }
    });
  } catch {
    /* no DOM */
  }
  return state;
}

export function sameCoachState(a: CoachState, b: CoachState): boolean {
  const ak = Object.keys(a);
  const bk = Object.keys(b);
  if (ak.length !== bk.length) return false;
  return ak.every((k) => a[k] === b[k]);
}

export interface CoachRectLike { top: number; left: number; width: number; height: number }

/**
 * The visible part of a rect. Section-sized anchors (a whole grid, a whole
 * pane) can be taller than the window; a card placed against the FULL rect
 * lands off-screen and the engine would otherwise scroll the page to fit the
 * element — which yanks the header away. Place against what is on screen.
 */
export function clipRectToViewport(rect: CoachRectLike, viewport: CoachViewportSize): CoachRectLike {
  const top = Math.max(0, rect.top);
  const left = Math.max(0, rect.left);
  const bottom = Math.min(viewport.height, rect.top + rect.height);
  const right = Math.min(viewport.width, rect.left + rect.width);
  return { top, left, width: Math.max(0, right - left), height: Math.max(0, bottom - top) };
}

/** Taller than most of the window: treat as a section, not a control. */
export const TALL_ANCHOR_RATIO = 0.6;
export function isTallAnchor(rect: { height: number }, viewport: CoachViewportSize): boolean {
  return rect.height > viewport.height * TALL_ANCHOR_RATIO;
}
